import { inputData } from "@/data/filtersConfig";
import { srConsoleInputsData } from "@/data/srConsoleInputsData";
import { Filters } from "@/pages/SearchResult/SearchPage";
import { DateTime } from "luxon";
import { useState, useRef } from "react";
import { PopupCustom } from "../Popup";
import SRSearchBar from "../SearchConsole/SRSearchBar";
import { useFilterStore } from "@/store/filterStore";


export const SearchPopup = ({ onClose = () => { } }: { onClose: () => void }) => {
    const [modalData, setModalData] = useState<{
        title: string;
        type?: string;
        content: any[];
    } | null>(null);
    const [activeFieldIndex, setActiveFieldIndex] = useState<number | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { currentFilters, setFilters } = useFilterStore();
    const formValues: Filters = currentFilters ?? {};

    const updateFormValues = (key: string, values: any[]) => {
        setFilters({ ...currentFilters, [key]: values });
    };

    srConsoleInputsData.map((input, index) => {
        input.clickHandler = () => {
            setActiveFieldIndex(index);
            setModalData({
                title: input.title,
                type: input.displayType,
                content: inputData[input.optionsKey as keyof typeof inputData]
            });
        };
    });

    const handleToggleCheckbox = (
        option: { label: string; value: string },
        group: { label: string; options: { label: string; value: string }[] },
        key: string
    ) => {
        const selected = (formValues[key as keyof Filters] || []) as { label: string; value: string }[];

        const isSingleOption = group.options.length === 1;
        const main = group.options[0];
        const children = group.options.slice(1);
        const allGroupValues = group.options.map(opt => opt.value);

        let updated = [...selected];

        const isMainClick = option.value === main.value;

        const hasMain = updated.some(v => v.value === main.value);
        const selectedChildren = updated.filter(v =>
            children.some(c => c.value === v.value)
        );
        const allChildrenSelected = children.length > 0 &&
            children.every(child => updated.some(v => v.value === child.value));

        // --- Одиночная группа (1 элемент) ---
        if (isSingleOption) {
            const exists = updated.some(v => v.value === main.value);
            updated = exists
                ? updated.filter(v => v.value !== main.value)
                : [...updated, main];
        }

        // --- Клик по главному чекбоксу группы ---
        else if (isMainClick) {
            if (hasMain) {
                updated = updated.filter(v => !allGroupValues.includes(v.value));
            } else {
                updated = updated.filter(v => !allGroupValues.includes(v.value));
                updated.push(main);
            }
        }

        // --- Клик по дочернему ---
        else {
            const alreadySelected = updated.some(v => v.value === option.value);

            if (hasMain || allChildrenSelected) {
                // снимаем главную и добавляем все кроме кликнутой
                updated = updated.filter(v => !allGroupValues.includes(v.value));
                updated = alreadySelected
                    ? updated // already removed by filter
                    : [...updated, option];
            } else {
                // обычное поведение: добавление / удаление
                updated = alreadySelected
                    ? updated.filter(v => v.value !== option.value)
                    : [...updated, option];

                // если теперь все дети выбраны — заменить на главный
                const nowAllSelected = children.every(child =>
                    updated.some(v => v.value === child.value)
                );

                if (nowAllSelected) {
                    updated = updated.filter(v => !children.some(c => c.value === v.value));
                    updated.push(main);
                }
            }
        }

        updateFormValues(key, updated);
    };

    return (
        <>
            <div className="container">
                <div className="full-screen-console" id="full-screen-console" tabIndex={0}>
                    <button className="close-btn" type="button" onClick={() => {
                        console.log('close');
                        setModalData(null);
                        setActiveFieldIndex(null);
                        onClose();
                    }}>
                        <i className="fa-solid fa-xmark"></i>
                    </button>

                    <div className="search-bar-wrapper" ref={wrapperRef}>
                        <SRSearchBar />
                    </div>
                </div>
            </div>

            {modalData && (
                <PopupCustom title={modalData.title} onClose={() => setModalData(null)}>
                    <div className="modal-content">
                        {modalData.type === 'checkbox-group' && (
                            <div className="checkbox-group">
                                {modalData.content.map((group, groupIndex) => {
                                    const inputKey = srConsoleInputsData[activeFieldIndex ?? 0].key;
                                    return (
                                        <fieldset key={groupIndex}>
                                            <legend>{group.label}</legend>
                                            {group.options.map((option: { label: string; value: string }) => {
                                                const main = group.options[0].value;
                                                const isMain = option.value === main;
                                                const selected = formValues[inputKey as keyof Filters] || [];
                                                const isChecked = selected.some(x => {
                                                    if (typeof x === 'string') return x === option.value;
                                                    if (typeof x === 'number') return Number(option.value) === x;
                                                    return x.value === option.value;
                                                });

                                                return (
                                                    <span key={option.value} className="checkbox-option" onClick={() => handleToggleCheckbox(option, group, inputKey)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="#313137">
                                                            {isChecked && (
                                                                <path d="M31.465,47.963,49.436,29.991a2.084,2.084,0,0,1,3.15,2.713l-.2.234L32.938,52.382a2.083,2.083,0,0,1-2.713.2l-.233-.2-8.334-8.333a2.084,2.084,0,0,1,2.713-3.15l.233.2,6.86,6.86L49.436,29.991Z" transform="translate(-12.02 -16.187)" />
                                                            )}
                                                            <path d="M45.361,3A7.639,7.639,0,0,1,53,10.639V45.361A7.639,7.639,0,0,1,45.361,53H10.639A7.639,7.639,0,0,1,3,45.361V10.639A7.639,7.639,0,0,1,10.639,3Zm0,3H10.639C8.721,6,6,8.721,6,10.639V45.361c0,1.918,2.721,4.713,4.639,4.713l34.722,0c1.918,0,4.713-2.792,4.713-4.71V10.639C50.074,8.72,47.278,6,45.36,6Z" transform="translate(-3 -3)" />
                                                        </svg>
                                                        {option.label}
                                                    </span>
                                                );
                                            })}
                                        </fieldset>
                                    );
                                })}
                            </div>
                        )}

                        {modalData.type === 'list' && (
                            <div className="modal-list">
                                {modalData.content.map((item: any, i: number) => {
                                    const key = srConsoleInputsData[activeFieldIndex!].key;
                                    const selectedList = formValues[key as keyof Filters] || [];
                                    const itemLabel = typeof item === 'string' ? item : item.label;

                                    const isChecked = selectedList.some((v: any) =>
                                        typeof v === 'string' ? v === itemLabel : v.label === itemLabel
                                    );

                                    const handleClick = () => {
                                        let updated = [...selectedList];

                                        if (isChecked) {
                                            updated = updated.filter((v: any) =>
                                                typeof v === 'string' ? v !== itemLabel : v.label !== itemLabel
                                            );
                                        } else {
                                            if (key === 'dates' && typeof item !== 'string') {
                                                updated.push({
                                                    label: item.label,
                                                    value: DateTime.fromFormat(item.label, 'MMMM yyyy').toFormat('yyyy-MM')
                                                });
                                            } else {
                                                updated.push(item);
                                            }
                                        }

                                        updateFormValues(key, updated);
                                    };

                                    return (
                                        <div key={i} className="checkbox-option" onClick={handleClick}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="#313137">
                                                {isChecked && (
                                                    <path d="M31.465,47.963,49.436,29.991a2.084,2.084,0,0,1,3.15,2.713l-.2.234L32.938,52.382a2.083,2.083,0,0,1-2.713.2l-.233-.2-8.334-8.333a2.084,2.084,0,0,1,2.713-3.15l.233.2,6.86,6.86L49.436,29.991Z" transform="translate(-12.02 -16.187)" />
                                                )}
                                                <path d="M45.361,3A7.639,7.639,0,0,1,53,10.639V45.361A7.639,7.639,0,0,1,45.361,53H10.639A7.639,7.639,0,0,1,3,45.361V10.639A7.639,7.639,0,0,1,10.639,3Zm0,3H10.639C8.721,6,6,8.721,6,10.639V45.361c0,1.918,2.721,4.713,4.639,4.713l34.722,0c1.918,0,4.713-2.792,4.713-4.71V10.639C50.074,8.72,47.278,6,45.36,6Z" transform="translate(-3 -3)" />
                                            </svg>
                                            <span>{itemLabel}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </PopupCustom>
            )}
        </>
    );
}